import { database } from './config';
import { ref, set, get, update, onValue, push, remove } from 'firebase/database';
import { questionService } from './questionService';
import type { GameState, Category, Question, Player, PlayerRole, FinalJeopardyState } from '../types/game';
import { categoryService } from './categoryService';

export const gameService = {
  // Create a new game
  async createGame(hostId: string, hostPassword: string): Promise<GameState> {
    const gameRef = push(ref(database, 'games'));
    const gameCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    
    // Get active categories and their questions from Firestore
    const categories = await questionService.getCategories();
    const activeCategories = categories.filter(cat => cat.isActive !== false);
    
    // For each category, get its questions
    const categoriesWithQuestions: Category[] = await Promise.all(
      activeCategories.map(async (category) => {
        const questions = await questionService.getQuestions();
        const categoryQuestionsRaw = questions.filter(q => q.categoryId === category.id && q.isActive !== false);

        // Group by difficulty
        const easy = categoryQuestionsRaw.filter(q => q.difficulty === 'easy');
        const medium = categoryQuestionsRaw.filter(q => q.difficulty === 'medium');
        const hard = categoryQuestionsRaw.filter(q => q.difficulty === 'hard');

        // Shuffle for randomness
        const shuffle = (arr: any[]) => arr.sort(() => Math.random() - 0.5);
        shuffle(easy);
        shuffle(medium);
        shuffle(hard);

        // Build questions for round 1 and round 2
        const round1Values = [100, 200, 300, 400, 500];
        const round2Values = [200, 400, 600, 800, 1000];
        // Round 1: $100, $200 = easy; $300, $400 = medium; $500 = hard
        const round1Questions = [
          easy[0], // $100
          easy[1], // $200
          medium[0], // $300
          medium[1], // $400
          hard[0], // $500
        ];
        // Round 2: $200, $400 = easy; $600, $800 = medium; $1000 = hard
        const round2Questions = [
          easy[2], // $200
          easy[3], // $400
          medium[2], // $600
          medium[3], // $800
          hard[1], // $1000
        ];

        // Helper to build question objects
        const buildQuestion = (q: any, value: number): Question | undefined => q ? {
          id: q.id!,
          text: q.text,
          answer: q.answer,
          value,
          categoryId: category.id,
          difficulty: q.difficulty || 'medium',
          choices: q.choices,
          isActive: q.isActive === undefined ? true : q.isActive,
          isFinalJeopardy: q.isFinalJeopardy === undefined ? false : q.isFinalJeopardy,
        } : undefined;

        // Use round 1 by default
        const questionsForCategory = round1Values
          .map((val, i) => buildQuestion(round1Questions[i], val))
          .filter((q): q is Question => !!q);

        return {
          id: category.id,
          name: category.name,
          description: category.description || '',
          questions: questionsForCategory,
          isActive: category.isActive === undefined ? true : category.isActive,
          hasFinalJeopardy: category.hasFinalJeopardy || false
        };
      })
    );

    const newGame: GameState = {
      id: gameRef.key!,
      code: gameCode,
      status: 'waiting',
      categories: categoriesWithQuestions,
      players: [],
      currentQuestion: null,
      hostId,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      hostPassword,
      settings: {
        audienceAnswerTime: 30, // Default 30 seconds for audience to answer
        showAudienceLeaderboard: true,
        allowAudienceParticipation: true,
      },
      round: 1,
    };

    await set(gameRef, newGame);
    return newGame;
  },

  // Get game by code
  async getGameByCode(code: string): Promise<GameState | null> {
    const gamesRef = ref(database, 'games');
    const snapshot = await get(gamesRef);
    const games = snapshot.val() as Record<string, GameState>;
    
    return Object.values(games).find(game => game.code === code) || null;
  },

  // Update game state
  async updateGame(gameId: string, updates: Partial<GameState>): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    await update(gameRef, {
      ...updates,
      updatedAt: Date.now(),
    });
  },

  // Add player to game
  async addPlayer(gameId: string, player: Player): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    const playersArray = Array.isArray(game.players)
      ? game.players
      : (game.players ? Object.values(game.players) : []);
    await update(gameRef, {
      players: [...playersArray, player],
      updatedAt: Date.now(),
    });
  },

  // Update player score
  async updatePlayerScore(gameId: string, playerId: string, score: number): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    
    const updatedPlayers = game.players.map(player => 
      player.id === playerId ? { ...player, score } : player
    );

    await update(gameRef, {
      players: updatedPlayers,
      updatedAt: Date.now(),
    });
  },

  // Submit audience answer
  async submitAudienceAnswer(
    gameId: string,
    playerId: string,
    questionId: string,
    answer: string
  ): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;

    if (!game.currentQuestion || !game.settings.allowAudienceParticipation) {
      throw new Error('Cannot submit answer at this time');
    }

    const currentQuestion = game.currentQuestion;
    const isCorrect = currentQuestion.question.choices 
      ? answer === currentQuestion.question.answer // For multiple choice, exact match
      : answer.toLowerCase() === currentQuestion.question.answer.toLowerCase(); // For text answers, case-insensitive
    
    // Update player's last answer
    const updatedPlayers = game.players.map(player => {
      if (player.id === playerId) {
        const questionValue = currentQuestion.question.value || 0;
        const newScore = isCorrect ? player.score + questionValue : player.score;
        return {
          ...player,
          score: newScore,
          lastAnswer: {
            questionId,
            answer,
            isCorrect,
            timestamp: Date.now(),
          },
        };
      }
      return player;
    });

    // Update audience answers for the current question
    const updatedAudienceAnswers = {
      ...currentQuestion.audienceAnswers,
      [playerId]: {
        answer,
        timestamp: Date.now(),
        isCorrect,
      },
    };

    await update(gameRef, {
      players: updatedPlayers,
      currentQuestion: {
        ...currentQuestion,
        audienceAnswers: updatedAudienceAnswers,
      },
      updatedAt: Date.now(),
    });
  },

  // Reveal question
  async revealQuestion(gameId: string, categoryId: string, questionId: string): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    
    const updatedCategories = game.categories.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          questions: category.questions.map(question => 
            question.id === questionId ? { ...question, revealed: true } : question
          ),
        };
      }
      return category;
    });

    const question = updatedCategories
      .find(cat => cat.id === categoryId)
      ?.questions.find(q => q.id === questionId);

    await update(gameRef, {
      categories: updatedCategories,
      currentQuestion: question ? {
        question,
        categoryName: updatedCategories.find(cat => cat.id === categoryId)?.name || '',
        timeRemaining: game.settings.audienceAnswerTime,
        audienceAnswers: {},
      } : null,
      updatedAt: Date.now(),
    });
  },

  // Subscribe to game updates
  subscribeToGame(gameId: string, callback: (game: GameState) => void): () => void {
    const gameRef = ref(database, `games/${gameId}`);
    const unsubscribe = onValue(gameRef, (snapshot) => {
      const game = snapshot.val() as GameState;
      callback(game);
    });
    return unsubscribe;
  },

  // Start Final Jeopardy
  async startFinalJeopardy(gameId: string): Promise<void> {
    // Fetch all Final Jeopardy questions
    const questions = await questionService.getFinalJeopardyQuestions();
    if (!questions.length) throw new Error('No Final Jeopardy questions available');
    // Pick a random question
    const randomIndex = Math.floor(Math.random() * questions.length);
    const q = questions[randomIndex];
    // Fetch categories to resolve category name
    const categories = await questionService.getCategories();
    const category = categories.find(cat => cat.id === q.categoryId);
    const categoryName = category ? category.name : q.categoryId;

    const gameRef = ref(database, `games/${gameId}`);
    await update(gameRef, {
      round: 3,
      finalJeopardy: {
        category: categoryName,
        question: q.text,
        answer: q.answer,
        isRevealed: false,
        isAnswered: false,
        timeRemaining: 30,
        wagers: {}
      },
      updatedAt: Date.now(),
    });
  },

  // Submit Final Jeopardy wager
  async submitFinalJeopardyWager(gameId: string, playerId: string, amount: number): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    
    if (!game.finalJeopardy) return;

    const currentWagers = game.finalJeopardy.wagers || {};
    await update(gameRef, {
      finalJeopardy: {
        ...game.finalJeopardy,
        wagers: {
          ...currentWagers,
          [playerId]: {
            ...currentWagers[playerId],
            amount
          }
        }
      },
      updatedAt: Date.now(),
    });
  },

  // Submit Final Jeopardy answer
  async submitFinalJeopardyAnswer(gameId: string, playerId: string, answer: string): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    
    if (!game.finalJeopardy) return;

    const currentWagers = game.finalJeopardy.wagers || {};
    await update(gameRef, {
      finalJeopardy: {
        ...game.finalJeopardy,
        wagers: {
          ...currentWagers,
          [playerId]: {
            ...currentWagers[playerId],
            answer
          }
        }
      },
      updatedAt: Date.now(),
    });
  },

  // Update Final Jeopardy score
  async updateFinalJeopardyScore(gameId: string, playerId: string, isCorrect: boolean): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    
    if (!game.finalJeopardy) return;

    const player = game.players.find(p => p.id === playerId);
    const wager = game.finalJeopardy.wagers[playerId];
    if (!player || !wager) return;

    const scoreChange = isCorrect ? wager.amount : -wager.amount;
    const newScore = Math.max(0, player.score + scoreChange);

    await update(gameRef, {
      finalJeopardy: {
        ...game.finalJeopardy,
        wagers: {
          ...game.finalJeopardy.wagers,
          [playerId]: {
            ...game.finalJeopardy.wagers[playerId],
            isCorrect
          }
        }
      },
      players: game.players.map(p => 
        p.id === playerId ? { ...p, score: newScore } : p
      ),
      updatedAt: Date.now(),
    });
  },

  // Complete Final Jeopardy
  async completeFinalJeopardy(gameId: string): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    await update(gameRef, {
      status: 'finished',
      finalJeopardy: {
        isAnswered: true
      },
      updatedAt: Date.now(),
    });
  },

  selectFinalJeopardyCategory: async (gameId: string): Promise<FinalJeopardyState> => {
    // Get all categories with Final Jeopardy questions
    const categories = await categoryService.getCategories();
    const finalJeopardyCategories = categories.filter(c => c.hasFinalJeopardy);
    
    if (finalJeopardyCategories.length === 0) {
      throw new Error('No Final Jeopardy categories found');
    }
    
    // Randomly select a category
    const selectedCategory = finalJeopardyCategories[Math.floor(Math.random() * finalJeopardyCategories.length)];
    
    // Get questions for this category
    const questions = await questionService.getQuestionsByCategory(selectedCategory.id);
    const finalJeopardyQuestions = questions.filter(q => q.isFinalJeopardy);
    
    if (finalJeopardyQuestions.length === 0) {
      throw new Error('No Final Jeopardy questions found in selected category');
    }
    
    // Randomly select a question
    const selectedQuestion = finalJeopardyQuestions[Math.floor(Math.random() * finalJeopardyQuestions.length)];
    
    // Initialize Final Jeopardy state
    const finalJeopardyState: FinalJeopardyState = {
      category: selectedCategory.name,
      question: selectedQuestion.text,
      answer: selectedQuestion.answer,
      wagers: {},
      isRevealed: false,
      timeRemaining: 30,
      isAnswered: false
    };
    
    // Update game state
    await gameService.updateGame(gameId, { finalJeopardy: finalJeopardyState });
    
    return finalJeopardyState;
  },

  // Submit audience Final Jeopardy wager
  async submitAudienceFinalJeopardyWager(
    gameId: string,
    playerId: string,
    amount: number
  ): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    
    if (!game.finalJeopardy || !game.settings.allowAudienceParticipation) {
      throw new Error('Cannot submit wager at this time');
    }

    const player = game.players.find(p => p.id === playerId);
    if (!player || player.role !== 'audience') {
      throw new Error('Invalid player');
    }

    // Ensure wager doesn't exceed player's score
    if (amount > player.score) {
      throw new Error('Wager cannot exceed current score');
    }

    const currentWagers = game.finalJeopardy.wagers || {};
    await update(gameRef, {
      finalJeopardy: {
        ...game.finalJeopardy,
        wagers: {
          ...currentWagers,
          [playerId]: {
            ...currentWagers[playerId],
            amount
          }
        }
      },
      updatedAt: Date.now(),
    });
  },

  // Submit audience Final Jeopardy answer
  async submitAudienceFinalJeopardyAnswer(
    gameId: string,
    playerId: string,
    answer: string
  ): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    
    if (!game.finalJeopardy || !game.settings.allowAudienceParticipation) {
      throw new Error('Cannot submit answer at this time');
    }

    const player = game.players.find(p => p.id === playerId);
    if (!player || player.role !== 'audience') {
      throw new Error('Invalid player');
    }

    // Map the selected choice to the actual answer
    const answerMap: { [key: string]: string } = {
      'A': game.finalJeopardy.answer,
      'B': 'Incorrect Answer 1',
      'C': 'Incorrect Answer 2',
      'D': 'Incorrect Answer 3'
    };

    const currentWagers = game.finalJeopardy.wagers || {};
    await update(gameRef, {
      finalJeopardy: {
        ...game.finalJeopardy,
        wagers: {
          ...currentWagers,
          [playerId]: {
            ...currentWagers[playerId],
            answer: answerMap[answer] || answer // Fallback to original answer if not in map
          }
        }
      },
      updatedAt: Date.now(),
    });
  },

  // Update audience Final Jeopardy score
  async updateAudienceFinalJeopardyScore(
    gameId: string,
    playerId: string,
    isCorrect: boolean
  ): Promise<void> {
    const gameRef = ref(database, `games/${gameId}`);
    const snapshot = await get(gameRef);
    const game = snapshot.val() as GameState;
    
    if (!game.finalJeopardy) return;

    const player = game.players.find(p => p.id === playerId);
    const wager = game.finalJeopardy.wagers[playerId];
    if (!player || !wager || player.role !== 'audience') return;

    const scoreChange = isCorrect ? wager.amount : -wager.amount;
    const newScore = Math.max(0, player.score + scoreChange);

    await update(gameRef, {
      finalJeopardy: {
        ...game.finalJeopardy,
        wagers: {
          ...game.finalJeopardy.wagers,
          [playerId]: {
            ...game.finalJeopardy.wagers[playerId],
            isCorrect
          }
        }
      },
      players: game.players.map(p => 
        p.id === playerId ? { ...p, score: newScore } : p
      ),
      updatedAt: Date.now(),
    });
  },
}; 